import { useState, useEffect, useMemo } from 'react';

interface UsePaginationOptions {
  defaultPerPage?: number;
  resetDeps?: unknown[];
}

interface UsePaginationReturn {
  currentPage: number;
  itemsPerPage: number;
  totalPages: number;
  startIndex: number;
  endIndex: number;
  setCurrentPage: (page: number) => void;
  setItemsPerPage: (perPage: number) => void;
  paginate: <T>(items: T[]) => T[];
  paginationProps: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    startIndex: number;
    endIndex: number;
    itemsPerPage: number;
    onPageChange: (page: number) => void;
    onItemsPerPageChange: (perPage: number) => void;
  };
}

export function usePagination(
  totalItems: number,
  options: UsePaginationOptions = {}
): UsePaginationReturn {
  const { defaultPerPage = 10, resetDeps = [] } = options;

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(defaultPerPage);

  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);

  // Reset to page 1 when filter dependencies change
  useEffect(() => {
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, resetDeps);

  // Clamp current page if it exceeds total pages (e.g. after deleting items)
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [totalItems, totalPages, currentPage]);

  const handleItemsPerPageChange = (perPage: number) => {
    setItemsPerPage(perPage);
    setCurrentPage(1);
  };

  const paginate = <T,>(items: T[]): T[] => items.slice(startIndex, endIndex);

  const paginationProps = useMemo(() => ({
    currentPage,
    totalPages,
    totalItems,
    startIndex,
    endIndex,
    itemsPerPage,
    onPageChange: setCurrentPage,
    onItemsPerPageChange: handleItemsPerPageChange,
  }), [currentPage, totalPages, totalItems, startIndex, endIndex, itemsPerPage]);

  return {
    currentPage,
    itemsPerPage,
    totalPages,
    startIndex,
    endIndex,
    setCurrentPage,
    setItemsPerPage: handleItemsPerPageChange,
    paginate,
    paginationProps,
  };
}
